import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { UserPlus, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from "./ui/dialog";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";

const SERVICE_CATEGORIES = [
  { value: "daily_personal_activities", label: "Daily personal activities" },
  { value: "assist_life_stage_transition", label: "Life stage transition" },
  { value: "assist_travel_transport", label: "Travel & transport" },
  { value: "assist_household_tasks", label: "Household tasks" },
  { value: "community_participation", label: "Community participation" },
  { value: "group_activities", label: "Group activities" },
  { value: "development_daily_life_skills", label: "Life skills" },
  { value: "therapeutic_supports", label: "Therapeutic supports" },
];

const PLAN_TYPES = [
  { value: "self_managed", label: "Self-managed" },
  { value: "plan_managed", label: "Plan-managed" },
  { value: "ndia_managed", label: "NDIA-managed" },
  { value: "private", label: "Private (non-NDIS)" },
];

const EMPTY = {
  email: "", password: "", name: "", phone: "", address: "",
  dob: "", ndis_number: "", ndis_plan_type: "plan_managed",
  plan_manager_name: "", plan_manager_email: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  hourly_rate: 75, location: "", state: "VIC",
  primary_service_category: "daily_personal_activities",
  languages: "English", bio: "",
};

/**
 * AdminUserCreator — button + dialog that lets admin spin up a new participant
 * or worker account. Skips email verification (mark_verified=true) so the user
 * can log in immediately.
 */
export function AdminUserCreator({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("participant");   // "participant" | "support_worker"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setForm(EMPTY);
    setType("participant");
  };

  const submit = async () => {
    if (!form.email.trim() || !form.name.trim() || !form.password) {
      return toast.error("Email, name and password are required");
    }
    if (form.password.length < 6) {
      return toast.error("Password must be at least 6 characters");
    }
    setSaving(true);
    try {
      const payload = { ...form, user_type: type };
      // Strip fields not relevant to the selected user type so the backend log stays clean
      if (type === "participant") {
        delete payload.hourly_rate;
        delete payload.primary_service_category;
        delete payload.languages;
        delete payload.bio;
      } else {
        delete payload.dob;
        delete payload.ndis_number;
        delete payload.ndis_plan_type;
        delete payload.plan_manager_name;
        delete payload.plan_manager_email;
        delete payload.emergency_contact_name;
        delete payload.emergency_contact_phone;
      }
      const r = await authFetch(`${API}/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        toast.success(`${type === "participant" ? "Participant" : "Worker"} account created — they can log in now.`);
        setOpen(false);
        reset();
        onCreated?.();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Couldn't create account");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="admin-create-user-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Create account
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create a new account</DialogTitle>
          <DialogDescription>
            Skips email verification. The user will be able to log in immediately with the password you set below.
          </DialogDescription>
        </DialogHeader>

        <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold mb-3">
          <button
            type="button"
            onClick={() => setType("participant")}
            className={`px-3 py-1.5 rounded-full transition-colors ${type === "participant" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
            data-testid="user-type-participant"
          >
            Participant
          </button>
          <button
            type="button"
            onClick={() => setType("support_worker")}
            className={`px-3 py-1.5 rounded-full transition-colors ${type === "support_worker" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
            data-testid="user-type-worker"
          >
            Support worker
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Full name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="admin-user-name" />
          <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="admin-user-email" />
          <Field label="Password *" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="admin-user-password" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="admin-user-phone" />
          <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="admin-user-address" className="md:col-span-2" />

          {type === "participant" && (
            <>
              <Field label="Date of birth" type="date" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} testId="admin-user-dob" />
              <Field label="NDIS number" value={form.ndis_number} onChange={(v) => setForm({ ...form, ndis_number: v })} testId="admin-user-ndis" />
              <SelectField label="Plan type" value={form.ndis_plan_type} onChange={(v) => setForm({ ...form, ndis_plan_type: v })} options={PLAN_TYPES} testId="admin-user-plan" />
              <Field label="Plan manager name" value={form.plan_manager_name} onChange={(v) => setForm({ ...form, plan_manager_name: v })} testId="admin-user-pm-name" />
              <Field label="Plan manager email" type="email" value={form.plan_manager_email} onChange={(v) => setForm({ ...form, plan_manager_email: v })} testId="admin-user-pm-email" />
              <Field label="Emergency contact name" value={form.emergency_contact_name} onChange={(v) => setForm({ ...form, emergency_contact_name: v })} testId="admin-user-ec-name" />
              <Field label="Emergency contact phone" value={form.emergency_contact_phone} onChange={(v) => setForm({ ...form, emergency_contact_phone: v })} testId="admin-user-ec-phone" />
            </>
          )}

          {type === "support_worker" && (
            <>
              <Field label="Location (city)" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId="admin-user-location" />
              <SelectField
                label="State"
                value={form.state}
                onChange={(v) => setForm({ ...form, state: v })}
                options={["VIC","NSW","QLD","SA","WA","TAS","NT","ACT"].map((s) => ({ value: s, label: s }))}
                testId="admin-user-state"
              />
              <Field label="Hourly rate ($)" type="number" value={form.hourly_rate} onChange={(v) => setForm({ ...form, hourly_rate: Number(v) })} testId="admin-user-rate" />
              <SelectField
                label="Primary service"
                value={form.primary_service_category}
                onChange={(v) => setForm({ ...form, primary_service_category: v })}
                options={SERVICE_CATEGORIES}
                testId="admin-user-service"
              />
              <Field label="Languages" value={form.languages} onChange={(v) => setForm({ ...form, languages: v })} testId="admin-user-langs" className="md:col-span-2" />
              <div className="md:col-span-2">
                <Label className="text-xs">Short bio</Label>
                <Textarea
                  rows={2}
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  className="mt-1"
                  placeholder="1-2 sentences workers can show on their profile."
                  data-testid="admin-user-bio"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => { setOpen(false); reset(); }} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="admin-user-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
            Create {type === "participant" ? "participant" : "worker"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", className = "", testId }) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testId} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
